import React, { useState, Fragment } from 'react'
import classNames from 'classnames'
import { RemoveItem } from '../remove-item'
import { PriceTypeHeader } from './header'
import { QuantityPicker } from './quantity-picker'
import { PriceTypeReasonsDropdown } from '../../../tickets/components/price-type-reasons-dropdown'

function GroupHeading({ nested, heading, category }) {
  return (
    <tr className="group-heading">
      <th
        scope="row"
        colSpan="4"
      >
        <PriceTypeHeader
          nested={nested}
          heading={heading}
          category={category}
        />
      </th>
    </tr>
  )
}

export default function PriceType({
  onCart = false,
  addOnPrice,
  cannotRemove = false,
  deleteCartItemTicket,
  dualLabel,
  emptyBody,
  hitMaximumsByAge,
  item,
  minimums,
  nested,
  stopDecrement,
  stopIncrement,
  noVacancy,
  remove,
  step,
  ticket,
  updateItem,
  useLabel,
  ruled,
  noPrice = false,
  hidePricingColumn
}) {
  const [didChange, setDidChange] = useState(false)

  const heading = getTitle({ ticket, useLabel, dualLabel })

  if (emptyBody) {
    return (
      <GroupHeading
        heading={heading}
        category={ticket.subLabel}
      />
    )
  }

  const cannotRemoveText = `You are unable to remove the above ticket(s) because another ticket in the cart
        requires its presence.`

  const cellClass = classNames(
    ticket.priceTypeReasons
      ? {
          'py-16 lg:py-20': !nested,
          'sm:pl-20 pt-20': nested,
          'pb-16 lg:pb-20': ruled || !nested
        }
      : ''
  )

  const ticketPrice = ticket.unitPrice || ticket.price
  const price = addOnPrice ? addOnPrice.price : ticketPrice

  const onRemoval = async () => {
    await deleteCartItemTicket(item, ticket)
  }

  const onChange = function (...args) {
    setDidChange(true)
    updateItem(...args)
  }

  const priceTypeReasonText = () => {
    const reason = ticket.priceTypeReasons.find(
      r => +r.id == +ticket.priceTypeReasonId
    )
    return reason.displayName || reason.name
  }

  const unitPrice = price.toFixed(2)
  const totalPrice = (price * ticket.quantity).toFixed(2)

  return (
    <Fragment>
      <tr data-testid="mobile-ticket-row-header">
        {/*header on separate line in mobile view per SHEDDWEB-1307*/}
        <th
          className={
            'ticket-header mobile-view pt-24' + (nested ? ' nested' : '')
          }
          colSpan="4"
        >
          <PriceTypeHeader
            nested={nested}
            heading={heading}
            category={ticket.subLabel}
          />
        </th>
      </tr>
      <tr
        data-testid={`${ticket.subLabel} ${heading}`}
        className={
          'ticket-row ' +
          (nested ? 'nested ' : '') +
          (ticket.priceTypeReasons ? 'border-none ' : '')
        }
      >
        <th
          className={
            'ticket-header' +
            cellClass +
            (ticket.priceTypeReasons ? ' border-none' : '')
          }
          scope="row"
        >
          <span className="mobile-hidden">
            <PriceTypeHeader
              nested={nested}
              heading={heading}
              category={ticket.subLabel}
            />
          </span>

          <span
            data-testid="quantity-picker-mobile"
            className="mobile-view"
          >
            <QuantityPicker
              hitMaximumsByAge={hitMaximumsByAge}
              item={item}
              productName={heading}
              minimums={minimums}
              stopDecrement={stopDecrement}
              stopIncrement={stopIncrement}
              noVacancy={noVacancy}
              step={step}
              ticket={ticket}
              onChange={onChange}
            />

            {didChange ? (
              <span
                className="sr-only"
                role="status"
                aria-atomic={true}
              >
                {heading} quantity is now {ticket.quantity}
                {ticket.quantity > 0 ? `totalling ${totalPrice} dollars` : null}
              </span>
            ) : null}
          </span>

          <RemoveItem
            remove={remove}
            cannotRemove={cannotRemove}
            cannotRemoveText={cannotRemoveText}
            onClick={onRemoval}
          />
        </th>
        <td
          data-testid="quantity-picker"
          className={
            'mobile-hidden ' +
            cellClass +
            (ticket.priceTypeReasons ? ' border-none' : '')
          }
        >
          <QuantityPicker
            hitMaximumsByAge={hitMaximumsByAge}
            item={item}
            productName={heading}
            minimums={minimums}
            stopDecrement={stopDecrement}
            stopIncrement={stopIncrement}
            noVacancy={noVacancy}
            step={step}
            ticket={ticket}
            onChange={onChange}
          />

          {didChange ? (
            <span
              className="sr-only"
              role="status"
              aria-atomic={true}
            >
              {heading} quantity is now {ticket.quantity}
              {ticket.quantity > 0 ? `totalling ${totalPrice} dollars` : null}
            </span>
          ) : null}
        </td>

        {!noPrice && !hidePricingColumn && (
          <React.Fragment>
            <td
              className={
                cellClass + (ticket.priceTypeReasons ? ' border-none' : '')
              }
            >
              <span
                data-testid="unit-price"
                className="unit-price"
              >
                ${unitPrice}
              </span>
              <span className="sr-only"> per ticket</span>
            </td>

            <td
              className={
                'font-bold ' +
                cellClass +
                (ticket.priceTypeReasons ? ' border-none' : '')
              }
            >
              <span
                data-testid="total-price"
                className="total-price"
              >
                ${totalPrice}
              </span>
              <span className="sr-only">
                {' '}
                for {ticket.quantity} {heading} tickets.
              </span>
            </td>
          </React.Fragment>
        )}
      </tr>

      {ticket.priceTypeReasons && (
        <tr>
          <td
            colSpan="4"
            className="pb-32 lg:pb-40"
          >
            {onCart ? (
              `${
                ticket.priceTypeReasonLabel
                  ? ticket.priceTypeReasonLabel + ': '
                  : ''
              }
                  ${priceTypeReasonText()}`
            ) : (
              <PriceTypeReasonsDropdown
                priceTypeReasons={ticket.priceTypeReasons}
                item={item}
                ticket={ticket}
                updateItem={updateItem}
              />
            )}
          </td>
        </tr>
      )}

      {/*
        Create a new table row for this text that is
        visible-only. The Screenreader version is with the
        remove button
      */}
      {cannotRemove && (
        <tr>
          <td
            colSpan="4"
            data-testid="cannot-remove"
            className="small-paragraph"
          >
            {cannotRemoveText}
          </td>
        </tr>
      )}
    </Fragment>
  )
}

const getTitle = ({ ticket, useLabel, dualLabel }) => {
  const { pricingGroup, subLabel, label } = ticket

  let title = subLabel

  if (useLabel) {
    title = label
  } else if (dualLabel && !['addon', 'default'].includes(pricingGroup)) {
    const groupLabel = pricingGroup.replace(/^\w/, c => c.toUpperCase())
    title = `${groupLabel}-${subLabel}`
  }

  return title
}
